import env from '@/config/env';

/** Supplies the user's BFF credentials, e.g. `{ Authorization: 'Bearer …' }`. */
export type BffAuthHeaders = () => Promise<Record<string, string>>;

let bffAuthHeaders: BffAuthHeaders | undefined;

/**
 * Called by the tRPC client setup, so the BFF's REST content routes are
 * fetched with exactly the credentials its procedures send.
 */
export function registerBffAuthHeaders(headers: BffAuthHeaders): void {
  bffAuthHeaders = headers;
}

/**
 * The BFF URL a download link names, or undefined when it names another origin
 * (a legacy presigned object-store URL). The BFF mounts its REST routes at its
 * own origin, so the canonical content path
 * `/omniwound/progress-note-artifacts/<id>/content` resolves against the BFF
 * (https://bff.dev.medintelekt.ai), never the SPA (https://dev.medintelekt.ai).
 */
export function bffContentUrl(url: string): URL | undefined {
  const bffOrigin = new URL(env.bffBaseUrl, window.location.origin).origin;
  const resolved = new URL(url, bffOrigin);
  return resolved.origin === bffOrigin ? resolved : undefined;
}

/**
 * Fetches a BFF content route. It authorizes every request (ADR 0208), so it
 * must carry the user's bearer token — which a tab navigation cannot send.
 */
export async function fetchBffContent(url: URL): Promise<Response> {
  if (!bffAuthHeaders) {
    throw new Error('Sign-in is not ready; try the download again');
  }
  return fetch(url, { cache: 'no-store', headers: await bffAuthHeaders() });
}
